import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useAuthStore } from '../stores';
import { AuthCard } from './AuthLayout';
function loadGoogleGisScript() {
    if (window.google?.accounts?.id)
        return Promise.resolve();
    const existing = document.querySelector('script[data-google-gis]');
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('Failed to load Google script')));
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.dataset.googleGis = 'true';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google script'));
        document.head.appendChild(script);
    });
}
export function Login() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const setAuth = useAuthStore((state) => state.setAuth);
    const googleButtonRef = useRef(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [externalProviders, setExternalProviders] = useState([]);
    const completeLogin = useCallback((data) => {
        setAuth(data.user, data.accessToken, data.refreshToken);
        navigate('/');
    }, [navigate, setAuth]);
    const handleGoogleCredential = useCallback(async (credential, providerName) => {
        setError('');
        setLoading(true);
        try {
            const response = await fetch('/api/auth/external', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider: providerName, idToken: credential }),
            });
            const rawText = await response.text();
            let parsed = null;
            if (rawText) {
                try {
                    parsed = JSON.parse(rawText);
                }
                catch {
                    parsed = null;
                }
            }
            if (!response.ok) {
                const errorMessage = parsed && 'message' in parsed ? parsed.message : undefined;
                throw new Error(errorMessage || t('auth.googleSignInFailed'));
            }
            if (!parsed || !('accessToken' in parsed)) {
                throw new Error(t('auth.googleSignInFailed'));
            }
            completeLogin(parsed);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('auth.googleSignInFailed'));
        }
        finally {
            setLoading(false);
        }
    }, [completeLogin, t]);
    useEffect(() => {
        let cancelled = false;
        fetch('/api/auth/external-providers')
            .then(async (r) => {
            if (!r.ok)
                return [];
            return (await r.json());
        })
            .then((list) => {
            if (!cancelled)
                setExternalProviders(Array.isArray(list) ? list : []);
        })
            .catch(() => {
            if (!cancelled)
                setExternalProviders([]);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        const googleProvider = externalProviders.find((p) => p.providerType.toLowerCase() === 'google' && p.clientId);
        if (!googleProvider || !googleButtonRef.current)
            return;
        let cancelled = false;
        loadGoogleGisScript()
            .then(() => {
            if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current)
                return;
            googleButtonRef.current.innerHTML = '';
            window.google.accounts.id.initialize({
                client_id: googleProvider.clientId,
                callback: (response) => {
                    void handleGoogleCredential(response.credential, googleProvider.name);
                },
                auto_select: false,
            });
            window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'outline',
                size: 'large',
                text: 'continue_with',
                shape: 'rectangular',
                width: 400,
            });
        })
            .catch(() => {
            if (!cancelled)
                setError(t('auth.googleSignInLoadFailed'));
        });
        return () => {
            cancelled = true;
        };
    }, [externalProviders, handleGoogleCredential, t]);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });
            const rawText = await response.text();
            let parsed = null;
            if (rawText) {
                try {
                    parsed = JSON.parse(rawText);
                }
                catch {
                    parsed = null;
                }
            }
            if (!response.ok) {
                const errorMessage = parsed && 'message' in parsed ? parsed.message : undefined;
                throw new Error(errorMessage || (rawText ? rawText : t('auth.loginFailed')));
            }
            if (!parsed) {
                throw new Error(rawText ? rawText : t('auth.loginFailed'));
            }
            completeLogin(parsed);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : t('auth.loginFailed'));
        }
        finally {
            setLoading(false);
        }
    };
    const hasGoogle = externalProviders.some((p) => p.providerType.toLowerCase() === 'google' && p.clientId);
    const facebookProvider = externalProviders.find((p) => p.providerType.toLowerCase() === 'facebook');
    const instagramProvider = externalProviders.find((p) => p.providerType.toLowerCase() === 'instagram');
    const hasMetaBrands = Boolean(facebookProvider || instagramProvider);
    const hasSocial = hasGoogle || hasMetaBrands;
    const handleMetaBrandClick = (brand) => {
        setError(t('auth.metaSignInComingSoon', {
            brand: brand === 'facebook' ? 'Facebook' : 'Instagram',
        }));
    };
    return (_jsxs(AuthCard, { title: t('auth.signInTitle'), subtitle: t('auth.signInSubtitle'), children: [error && _jsx("div", { className: "auth-error", children: error }), hasSocial && (_jsxs("div", { className: "auth-social", children: [hasMetaBrands && (_jsxs("div", { className: "auth-social-icons", children: [facebookProvider && (_jsx("button", { type: "button", className: "auth-social-icon-btn auth-social-facebook", onClick: () => handleMetaBrandClick('facebook'), disabled: loading, "aria-label": t('auth.continueWithFacebook'), title: t('auth.continueWithFacebook'), children: _jsx("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", width: "22", height: "22", children: _jsx("path", { fill: "currentColor", d: "M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.54-4.7 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.5 0-1.96.93-1.96 1.89v2.26h3.34l-.53 3.49h-2.81V24C19.61 23.1 24 18.1 24 12.07z" }) }) })), instagramProvider && (_jsx("button", { type: "button", className: "auth-social-icon-btn auth-social-instagram", onClick: () => handleMetaBrandClick('instagram'), disabled: loading, "aria-label": t('auth.continueWithInstagram'), title: t('auth.continueWithInstagram'), children: _jsx("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", width: "22", height: "22", children: _jsx("path", { fill: "currentColor", d: "M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.64-.07-4.85s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 2.7.27.27 2.7.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95C23.73 2.7 21.31.27 16.95.07 15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zM12 16a4 4 0 110-8 4 4 0 010 8zm6.41-11.85a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z" }) }) }))] })), hasGoogle && _jsx("div", { ref: googleButtonRef, className: "auth-google-button" }), _jsx("div", { className: "auth-divider", children: t('common.or') })] })), _jsxs("form", { onSubmit: handleSubmit, className: "auth-form", children: [_jsxs("div", { className: "auth-field", children: [_jsx("label", { htmlFor: "email", className: "auth-label", children: t('auth.email') }), _jsx("input", { id: "email", type: "email", value: email, onChange: (e) => setEmail(e.target.value), required: true, autoComplete: "email", className: "auth-input", placeholder: t('auth.emailPlaceholder') })] }), _jsxs("div", { className: "auth-field", children: [_jsx("label", { htmlFor: "password", className: "auth-label", children: t('auth.password') }), _jsx("input", { id: "password", type: "password", value: password, onChange: (e) => setPassword(e.target.value), required: true, autoComplete: "current-password", className: "auth-input", placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" })] }), _jsx("button", { type: "submit", disabled: loading, className: "auth-button", children: loading ? t('auth.signingIn') : t('auth.signIn') })] }), _jsxs("div", { className: "auth-footer", children: [t('auth.noAccount'), ' ', _jsx(Link, { to: "/register", className: "auth-link", children: t('auth.signUp') })] })] }));
}
